import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "otraquizz_install_dismissed_at";
// Re-show the banner this many days after the user dismissed it.
const REPROMPT_AFTER_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  // iOS Safari sets navigator.standalone when the app is added to home screen.
  return mm || window.navigator.standalone === true;
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  // Chrome / Firefox on iOS embed "CriOS" / "FxiOS" — they cannot install PWAs.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < REPROMPT_AFTER_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPwaBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isMobile()) return;
    if (recentlyDismissed()) return;

    // Android / desktop Chrome flow
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari: no event — show our manual banner.
    if (isIOSSafari()) {
      setShow(true);
    }

    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // localStorage may be disabled — banner just won't be persisted.
    }
    setShow(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (isIOSSafari()) {
      // No programmatic install on iOS — open the help sheet.
      setShowIosHelp(true);
      return;
    }
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        setShow(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    }
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <>
      <div
        data-testid="install-pwa-banner"
        role="dialog"
        aria-label="Installer l'application OTRAQUIZZ"
        className="fixed left-2 right-2 sm:left-auto sm:right-4 sm:max-w-sm bottom-3 z-40 panel border border-cyan-400/30 rounded-2xl px-4 py-3 shadow-xl backdrop-blur-xl"
        style={{
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="shrink-0 w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center"
            style={{
              boxShadow: "0 0 18px rgba(0, 229, 255, 0.45)",
            }}
          >
            <Download className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold uppercase tracking-tight text-sm text-white">
              Installer OTRAQUIZZ
            </div>
            <p className="text-[12px] text-white/65 leading-snug mt-0.5">
              Ajoute l&apos;app à ton écran d&apos;accueil pour jouer plus vite, plein écran.
            </p>
            <div className="flex items-center gap-2 mt-2.5">
              <button
                data-testid="install-pwa-confirm"
                onClick={install}
                className="px-3 py-1.5 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-wider text-[11px] active:scale-95 transition-all"
              >
                {isIOSSafari() ? "Voir comment" : "Installer"}
              </button>
              <button
                data-testid="install-pwa-dismiss"
                onClick={dismiss}
                className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/70 text-[11px] uppercase tracking-wider transition-all"
              >
                Plus tard
              </button>
            </div>
          </div>
          <button
            data-testid="install-pwa-close"
            onClick={dismiss}
            aria-label="Fermer"
            className="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/60 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIosHelp && (
        <div
          data-testid="install-pwa-ios-help"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowIosHelp(false);
          }}
        >
          <div className="w-full max-w-md panel border border-cyan-400/30 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display uppercase tracking-tight text-base text-cyan-300">
                Installer sur iPhone
              </h3>
              <button
                onClick={() => setShowIosHelp(false)}
                aria-label="Fermer"
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/60"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-white/80">
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center font-mono-stat text-cyan-300 text-xs">
                  1
                </span>
                <span className="leading-snug pt-1">
                  Appuie sur <Share className="inline w-4 h-4 mx-1 -mt-0.5 text-cyan-300" /> Partager dans la barre Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center font-mono-stat text-cyan-300 text-xs">
                  2
                </span>
                <span className="leading-snug pt-1">
                  Choisis « Sur l&apos;écran d&apos;accueil » <Plus className="inline w-4 h-4 mx-1 -mt-0.5 text-cyan-300" />.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center font-mono-stat text-cyan-300 text-xs">
                  3
                </span>
                <span className="leading-snug pt-1">
                  Valide « Ajouter » — OTRAQUIZZ apparaît comme une vraie app sur ton écran d&apos;accueil.
                </span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-5 w-full px-4 py-2.5 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-wider text-xs active:scale-95 transition-all"
            >
              C&apos;est compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
